import type { RetrofitName } from '@/shared/types/assessment.types'
import type { CustomAssessmentInput } from '@/shared/types/custom-assessment-input.types'
import type { ISFPExtractedData } from '@/shared/types/isfp.types'
import { httpClient } from '@/shared/utils/http-client'

const EB_HUB_API_BASE_URL = import.meta.env.DEV
  ? '/eb-hub-service/eb-hub/v1'
  : 'https://eb-hub-service.enter.de/eb-hub/v1'

export interface ISFPExtractedDataResponse {
  data: ISFPExtractedData
}

export interface CustomAssessmentInputResponse {
  data: CustomAssessmentInput
}

export interface CombinedDataPayload {
  isfpExtractedData: ISFPExtractedData
  customAssessmentInput: CustomAssessmentInput
  buildingId: string
}

class ISFPDataApi {
  /**
   * Get existing ISFP data for a specific building
   * @param buildingUuid - The UUID of the building
   * @returns Promise with ISFP data
   */
  async getISFPData(buildingUuid: string): Promise<ISFPExtractedData> {
    const url = `${EB_HUB_API_BASE_URL}/isfp/${buildingUuid}`
    const response = await httpClient.get<ISFPExtractedDataResponse>(url)
    return response.data
  }

  /**
   * Submit confirmed ISFP data to eb-hub backend
   * @param buildingUuid - The UUID of the building
   * @param data - The confirmed ISFP data
   * @returns Promise resolving when submission is complete
   */
  async submitISFPData(buildingUuid: string, data: ISFPExtractedData): Promise<void> {
    const url = `${EB_HUB_API_BASE_URL}/isfp/${buildingUuid}`
    return await httpClient.post<void>(url, data)
  }

  /**
   * Get optional ISFP fields default values for a specific building
   * @param buildingUuid - The UUID of the building
   * @returns Promise with custom assessment input data
   */
  async getCustomAssessmentInput(buildingUuid: string): Promise<CustomAssessmentInput> {
    const url = `${EB_HUB_API_BASE_URL}/custom-assessment-input/${buildingUuid}`
    const response = await httpClient.get<CustomAssessmentInputResponse>(url)
    return response.data
  }

  /**
   * Submit custom assessment input to eb-hub backend
   * @param buildingUuid - The UUID of the building
   * @param data - The custom assessment input data
   * @returns Promise resolving when submission is complete
   */
  async submitCustomAssessmentInput(
    buildingUuid: string,
    data: CustomAssessmentInput
  ): Promise<void> {
    const url = `${EB_HUB_API_BASE_URL}/custom-assessment-input/${buildingUuid}`
    return await httpClient.post<void>(url, data)
  }

  /**
   * Submit combined ISFP data and custom assessment input to eb-hub backend
   * @param data - Combined data payload with ISFP data, custom assessment input, and building ID
   * @returns Promise resolving when submission is complete
   */
  async submitCombinedData(data: CombinedDataPayload): Promise<void> {
    const url = `${EB_HUB_API_BASE_URL}/combined-data`
    return await httpClient.post<void>(url, data)
  }

  /**
   * Update hidden retrofits for a specific building
   * @param buildingUuid - The UUID of the building
   * @param hiddenRetrofits - Array of retrofit names to hide
   * @returns Promise resolving when update is complete
   */
  async updateHiddenRetrofits(
    buildingUuid: string,
    hiddenRetrofits: RetrofitName[]
  ): Promise<void> {
    const url = `${EB_HUB_API_BASE_URL}/assessment/${buildingUuid}/hidden-retrofits`
    return await httpClient.put<void>(url, { hiddenRetrofits })
  }
}

export const isfpDataApi = new ISFPDataApi()
